import { Injectable, inject } from '@angular/core';

import { map } from 'rxjs';

import { I18nService } from '@core/i18n-legacy';
import { injectReportsTranslations } from '@core/reports-contracts';
import {
  DetailsBreadcrumbsParams,
  DetailsBreadcrumbsService,
  FilterGroup,
  ReportsFilterBreadcrumb,
} from '@core/reports-types';

@Injectable()
export class DetailsFunnelBreadcrumbsService implements DetailsBreadcrumbsService {
  private i18n = inject(I18nService);
  private reportsTranslations = injectReportsTranslations();

  public getBreadcrumbs(params: DetailsBreadcrumbsParams): ReportsFilterBreadcrumb[] {
    switch (params.breadcrumbsSettings.primaryGroup) {
      case 'stage-name':
        return this.stageName(params);

      case 'stage-category': {
        return this.stageCategory(params);
      }

      default:
        return [];
    }
  }

  private stageName({
    dimensions,
    breadcrumbsSettings,
  }: DetailsBreadcrumbsParams): ReportsFilterBreadcrumb[] {
    const breadcrumbs: ReportsFilterBreadcrumb[] = [];

    if (dimensions.secondary) {
      const translatedValue =
        dimensions.secondary.length > 1
          ? this.i18n.translate('tlr.group_name.other')
          : dimensions.secondary[0].name;

      breadcrumbs.push({
        simple: true,
        bold: true,
        hasOverwrites: false,
        translatedValue,
      });
    }

    if (breadcrumbsSettings.secondaryGroup) {
      const secondaryGroup = breadcrumbsSettings.secondaryGroup.split(':');
      const [group, value]: string[] = secondaryGroup;
      const filter$ = this.reportsTranslations.translateFilter(group, value);

      breadcrumbs.push({
        simple: false,
        translatedGroup: filter$.pipe(map(({ group }) => group)),
        translatedValues: [filter$],
        hasOverwrites: false,
        invalid: false,
      });
    }

    breadcrumbs.push({
      simple: false,
      translatedGroup: this.reportsTranslations.translateGroup(FilterGroup.StageName),

      translatedValues: [{ label: dimensions.primary.name }],
      hasOverwrites: false,
      invalid: false,
    });

    return breadcrumbs;
  }

  private stageCategory({
    breadcrumbsSettings,
    dimensions,
  }: DetailsBreadcrumbsParams): ReportsFilterBreadcrumb[] {
    const breadcrumbs: ReportsFilterBreadcrumb[] = [];
    const primaryGroup = this.reportsTranslations.translateGroup(breadcrumbsSettings.primaryGroup);

    breadcrumbs.push({
      simple: false,
      translatedGroup: primaryGroup,
      translatedValues: [{ label: dimensions.primary.name }],
      hasOverwrites: false,
      invalid: false,
    });

    if (
      dimensions.secondary &&
      ['dropoff_rate', 'proceed_rate'].includes(breadcrumbsSettings.metric)
    ) {
      const translatedValue =
        dimensions.secondary.length > 1
          ? this.i18n.translate('tlr.group_name.other')
          : dimensions.secondary[0].name;

      breadcrumbs.push({
        simple: true,
        bold: true,
        translatedValue,
        hasOverwrites: false,
      });
    }

    return breadcrumbs;
  }
}
